import { ReactNode, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import Iconify from 'src/components/iconify';
import { useNonBrandMetricEvolutionQuery } from 'src/context/api/non-branded/api';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { RootState } from 'src/context/store';
import { useLocales } from 'src/locales';
import ExploreChart, { getMetricEvolutionColor } from '../../explore/ExploreChart';
import ChartExportModal from 'src/components/chart/chart-export-modal';
import { useNonBrandFilterArgs } from '../use-non-brand-filter-args';
import { isLeadWorkspace } from '../workspace-type';
import {
  CompareMode,
  DEFAULT_METRICS,
  Granularity,
  MetricKey,
  analysisRange,
  buildChartToggle,
  compareRange,
  isAvailableAt,
  metricsForTemplate,
} from './catalog';
import { ComparisonValue, exportMetricEvolutionChart } from './export-chart';

type NbMetricEvolutionModalProps = {
  open: boolean;
  onClose: () => void;
};

type Option<T> = { value: T; label: string };

const MAX_METRICS = 3;
const MONTHLY_ONLY_HINT = 'Change the granularity to Monthly for this metric';

const GRANULARITY_OPTIONS: Option<Granularity>[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];
/** Monthly by default: the page is monthly and Non brand searches (Metric 1) has no weekly value. */
const DEFAULT_GRANULARITY: Granularity = 'monthly';
const DEFAULT_AMOUNT: Record<Granularity, number> = { weekly: 2, monthly: 6 };

const periodOptions = (granularity: Granularity): Option<number>[] => {
  const unit = granularity === 'weekly' ? 'week' : 'month';
  return Array.from({ length: 12 }, (_, index) => ({
    value: index + 1,
    label: index === 0 ? `Last ${unit}` : `Last ${index + 1} ${unit}s`,
  }));
};

/** Midday UTC, so that ExploreChart's UTC formatting of the compared period keeps the local day. */
const toUtcNoonISOString = (date: Date): string =>
  new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12)).toISOString();

const apiDate = (date: Date) => format(date, 'yyyy-MM-dd');

/** Selected metrics plus one empty slot, up to MAX_METRICS. */
const withEmptySlot = (metrics: MetricKey[]): (MetricKey | '')[] =>
  metrics.length >= MAX_METRICS ? metrics.slice(0, MAX_METRICS) : [...metrics, ''];

const CHECKBOX_SX = {
  color: '#C4CDD5',
  '&.Mui-checked': { color: '#118D57' },
  '&:hover, &.Mui-checked:hover': { backgroundColor: 'transparent' },
};
const CHECKBOX_LABEL_SX = {
  m: 0,
  '& .MuiFormControlLabel-label': {
    color: '#637381',
    fontFamily: 'Public Sans',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '100%',
  },
};
const CHIP_SX = {
  display: 'flex',
  alignItems: 'center',
  height: '28px',
  px: '12px',
  borderRadius: '8px',
  bgcolor: '#F4F6F8',
  color: '#333C47',
  fontFamily: 'Public Sans',
  fontSize: '13px',
  fontWeight: 500,
  lineHeight: '23px',
  whiteSpace: 'nowrap',
};

/** Granularity / period picker of the header: a grey chip in a rounded box, as in the Brand modals. */
function ChipSelect<T>({
  ariaLabel,
  width,
  options,
  value,
  onChange,
}: {
  ariaLabel: string;
  width: number;
  options: Option<T>[];
  value: Option<T>;
  onChange: (value: Option<T>) => void;
}) {
  return (
    <Autocomplete
      disableClearable
      options={options}
      value={value}
      onChange={(_, next) => next && onChange(next)}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, current) => option.value === current.value}
      popupIcon={<Iconify icon="eva:chevron-down-fill" width={18} sx={{ color: '#91A0AF' }} />}
      sx={{
        width,
        '& .MuiOutlinedInput-root': { height: 40, minHeight: 40, p: '4px 8px', borderRadius: '12px' },
        '& .MuiAutocomplete-input': { width: 0, minWidth: 0, opacity: 0, p: '0 !important' },
        '& .MuiAutocomplete-endAdornment': { right: 8 },
      }}
      slotProps={{
        paper: {
          sx: {
            mt: '4px',
            borderRadius: '8px',
            boxShadow: '0px 4px 12px rgba(145, 158, 171, 0.24)',
            '& .MuiAutocomplete-listbox': { maxHeight: '260px' },
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputProps={{ ...params.inputProps, readOnly: true, 'aria-label': ariaLabel }}
          InputProps={{ ...params.InputProps, startAdornment: <Box sx={CHIP_SX}>{value.label}</Box> }}
        />
      )}
    />
  );
}

function CheckboxLabel({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <FormControlLabel
      control={<Checkbox size="small" checked={checked} onChange={(event) => onChange(event.target.checked)} sx={CHECKBOX_SX} />}
      label={label}
      sx={CHECKBOX_LABEL_SX}
    />
  );
}

/** Orange warning shown instead of a metric's value when the granularity is weekly. */
function MonthlyOnlyIcon({ children }: { children?: ReactNode }) {
  return (
    <Tooltip title={MONTHLY_ONLY_HINT} placement="right">
      <Box component="span" aria-label={MONTHLY_ONLY_HINT} sx={{ display: 'inline-flex', color: '#B76E00' }}>
        <Iconify icon="solar:danger-triangle-linear" width={18} />
        {children}
      </Box>
    </Tooltip>
  );
}

/**
 * "Metric evolution" modal of the Non Brand Search Performance grid: up to 3 metrics of the grid, summed over the
 * filtered search terms, per week or month, with an optional comparison. Same layout as the Brand Conversions modal
 * (ExploreChart, export); its range, granularity and comparison are local, not the page's.
 */
export default function NbMetricEvolutionModal({ open, onClose }: NbMetricEvolutionModalProps) {
  const { t } = useLocales();
  const workspace = useCurrentWorkspace();
  const isLead = isLeadWorkspace(workspace?.template);
  const metrics = useMemo(() => metricsForTemplate(isLead), [isLead]);
  const filterArgs = useNonBrandFilterArgs();
  const opportunity = useSelector((state: RootState) => state.filters.nonBrandOpportunity);

  const [granularity, setGranularity] = useState<Granularity>(DEFAULT_GRANULARITY);
  const [amount, setAmount] = useState(DEFAULT_AMOUNT[DEFAULT_GRANULARITY]);
  const [compareMode, setCompareMode] = useState<CompareMode>('none');
  const [slots, setSlots] = useState<(MetricKey | '')[]>(withEmptySlot(DEFAULT_METRICS));
  // Selected but not drawn (eye button)
  const [hiddenMetrics, setHiddenMetrics] = useState<MetricKey[]>([]);
  const [showLabels, setShowLabels] = useState(false);
  const [matchScale, setMatchScale] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const chartInstanceRef = useRef<any>(null);

  // Each opening starts again from the default range, without comparison (adjusted while rendering, so that the
  // previous range is never requested). The metrics are kept, like in the Brand modals.
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (open) {
      setGranularity(DEFAULT_GRANULARITY);
      setAmount(DEFAULT_AMOUNT[DEFAULT_GRANULARITY]);
      setCompareMode('none');
    }
  }
  // Another workspace may not have the same metrics (ROAS vs CPA)
  const [metricsWorkspaceKey, setMetricsWorkspaceKey] = useState(`${workspace?.id}-${isLead}`);
  if (metricsWorkspaceKey !== `${workspace?.id}-${isLead}`) {
    setMetricsWorkspaceKey(`${workspace?.id}-${isLead}`);
    setSlots(withEmptySlot(DEFAULT_METRICS));
    setHiddenMetrics([]);
  }

  // Today is read again on each opening
  const range = useMemo(() => analysisRange(granularity, amount), [granularity, amount, open]); // eslint-disable-line react-hooks/exhaustive-deps
  const comparedRange = useMemo(() => compareRange(range, granularity, amount, compareMode), [range, granularity, amount, compareMode]);

  const { currentData, isFetching } = useNonBrandMetricEvolutionQuery(
    {
      ...filterArgs,
      start_date: apiDate(range.start),
      end_date: apiDate(range.end),
      opportunity,
      timestep: granularity === 'weekly' ? 'year_week' : 'year_month',
      compare: !!comparedRange,
      ...(comparedRange ? { start_compare: apiDate(comparedRange.start), end_compare: apiDate(comparedRange.end) } : {}),
    },
    { skip: !open || !workspace?.id }
  );

  const selectedMetrics = slots.filter(Boolean) as MetricKey[];
  // Drawn: selected metrics that have values at this granularity (the eye-hidden ones are dropped by ExploreChart)
  const chartMetrics = selectedMetrics.filter((key) => isAvailableAt(key, granularity));
  const visibleMetrics = chartMetrics.filter((key) => !hiddenMetrics.includes(key));

  const toggle = useMemo(
    () => buildChartToggle({ data: currentData, metrics, visibleMetrics: chartMetrics, granularity, isFetching, t }),
    // chartMetrics is rebuilt on every render: depend on its content
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentData, metrics, chartMetrics.join(), granularity, isFetching, t]
  );
  // What ExploreChart reads from its filterStore prop
  const chartFilters = useMemo(
    () => ({
      analysisChartTimestep: granularity === 'weekly' ? 'year_week' : 'year_month',
      analysisChartCompareMode: compareMode,
      analysisChartCompareStart: comparedRange ? toUtcNoonISOString(comparedRange.start) : '',
      analysisChartCompareEnd: comparedRange ? toUtcNoonISOString(comparedRange.end) : '',
    }),
    [granularity, compareMode, comparedRange]
  );

  const stats = toggle.stats;
  const titleOf = (key: MetricKey) => stats.find((stat) => stat.key === key)?.title ?? key;
  const colorOf = (key: MetricKey) => {
    const stat = stats.find((item) => item.key === key);
    return getMetricEvolutionColor(key, stat?.title ?? '', stat?.color ?? '#919EAB');
  };

  const changeGranularity = (next: Granularity) => {
    setGranularity(next);
    setAmount(DEFAULT_AMOUNT[next]);
  };

  const changeMetric = (index: number, next: MetricKey | '') => {
    const previous = slots[index];
    // The last selected metric cannot be removed; a monthly-only metric cannot be picked on weekly buckets
    if (!next && selectedMetrics.length <= 1) return;
    if (next && !isAvailableAt(next, granularity)) return;
    if (previous && previous !== next) setHiddenMetrics((current) => current.filter((key) => key !== previous));
    const nextSlots = [...slots];
    nextSlots[index] = next;
    setSlots(withEmptySlot(nextSlots.filter(Boolean) as MetricKey[]));
  };

  const toggleMetricVisibility = (key: MetricKey) => {
    if (hiddenMetrics.includes(key)) {
      setHiddenMetrics((current) => current.filter((item) => item !== key));
    } else if (visibleMetrics.length > 1) {
      // The last drawn series cannot be hidden
      setHiddenMetrics((current) => [...current, key]);
    }
  };

  // Evolution (%) of each drawn metric per bucket, for the PNG export (main and compare points paired by position)
  const comparisonValues = useMemo<ComparisonValue[][]>(() => {
    const main = toggle.statsData?.time_series_data ?? [];
    const compared = toggle.compareStatsData.time_series_data;
    if (compareMode === 'none' || main.length === 0 || compared.length === 0) return [];
    const drawn = visibleMetrics;
    return main.map((bucket: any, index: number) =>
      drawn.map((key) => {
        const current = Number(bucket?.[key]) || 0;
        const previous = Number(compared[index]?.[key]) || 0;
        let value: number | null = null;
        if (previous !== 0) value = ((current - previous) / Math.abs(previous)) * 100;
        else if (current === 0) value = 0;
        return { metricKey: key, color: colorOf(key), value };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggle, compareMode, visibleMetrics.join()]);

  const handleExport = async (exportFormat: any, options: any) => {
    const exported = await exportMetricEvolutionChart({
      chartInstance: chartInstanceRef.current,
      format: exportFormat,
      options,
      legendItems: visibleMetrics.map((key) => ({ label: titleOf(key), color: colorOf(key) })),
      comparisonValues,
    });
    if (exported) setExportModalOpen(false);
  };

  const renderMetricSelect = (value: MetricKey | '', index: number) => {
    const label = `Metric ${index + 1}`;
    const unavailable = Boolean(value) && !isAvailableAt(value as MetricKey, granularity);
    const isHidden = Boolean(value) && hiddenMetrics.includes(value as MetricKey);
    const cannotHide = Boolean(value) && !isHidden && visibleMetrics.length <= 1;
    return (
      <FormControl key={`metric-slot-${index}`} fullWidth sx={{ minWidth: 0 }}>
        <Box sx={{ position: 'relative', pt: '8px' }}>
          {/* Label on the field border */}
          <Typography
            component="span"
            sx={{
              position: 'absolute',
              top: 0,
              left: '12px',
              zIndex: 1,
              px: '4px',
              backgroundColor: '#FFFFFF',
              color: '#91A0AF',
              fontFamily: 'Public Sans',
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: '12px',
            }}
          >
            {label}
          </Typography>
          <Autocomplete
            fullWidth
            disableClearable
            options={stats}
            value={stats.find((stat) => stat.key === value) ?? (null as any)}
            onChange={(_, next) => changeMetric(index, (next?.key as MetricKey) ?? '')}
            getOptionLabel={(option) => option?.title ?? ''}
            isOptionEqualToValue={(option, current) => option.key === current?.key}
            // Already picked in another slot
            filterOptions={(options, state) =>
              options.filter(
                (option) =>
                  (option.key === value || !selectedMetrics.includes(option.key as MetricKey)) &&
                  option.title.toLowerCase().includes(state.inputValue.toLowerCase().trim())
              )
            }
            popupIcon={<Iconify icon="eva:chevron-down-fill" width={20} sx={{ color: '#91A0AF' }} />}
            slotProps={{
              paper: {
                sx: {
                  mt: '6px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0px 4px 12px rgba(145, 158, 171, 0.24)',
                },
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': { height: '46px', minHeight: '46px', pr: '70px !important' },
              '& .MuiAutocomplete-input': {
                fontFamily: 'Public Sans',
                fontSize: '16px',
                fontWeight: 400,
                color: isHidden || unavailable ? '#919EAB' : '#333C47',
              },
              '& .MuiAutocomplete-endAdornment': { right: '12px' },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select metric"
                inputProps={{ ...params.inputProps, 'aria-label': label }}
              />
            )}
            renderOption={(props, option, { selected }) => {
              const optionUnavailable = !isAvailableAt(option.key as MetricKey, granularity);
              return (
                <Box
                  component="li"
                  {...props}
                  key={option.key}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Public Sans',
                      fontSize: '14px',
                      color: optionUnavailable ? '#919EAB' : '#333C47',
                    }}
                  >
                    {option.title}
                  </Typography>
                  {optionUnavailable && <MonthlyOnlyIcon />}
                  {!optionUnavailable && selected && <Iconify icon="eva:checkmark-fill" width={16} sx={{ color: '#333C47' }} />}
                </Box>
              );
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              right: '38px',
              top: 'calc(50% + 4px)',
              transform: 'translateY(-50%)',
              zIndex: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
            }}
          >
            {unavailable ? (
              <MonthlyOnlyIcon />
            ) : (
              <IconButton
                size="small"
                aria-label={`${isHidden ? 'Show' : 'Hide'} ${value ? titleOf(value as MetricKey) : label}`}
                disabled={!value || cannotHide}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (value) toggleMetricVisibility(value as MetricKey);
                }}
                sx={{
                  width: 32,
                  height: 32,
                  p: 0,
                  color: isHidden ? '#C4CDD5' : '#91A0AF',
                  '&:hover': { backgroundColor: 'rgba(145, 158, 171, 0.08)' },
                  '&.Mui-disabled': { color: '#C4CDD5' },
                }}
              >
                <Iconify icon={isHidden ? 'solar:eye-closed-linear' : 'solar:eye-linear'} width={22} />
              </IconButton>
            )}
          </Box>
        </Box>
      </FormControl>
    );
  };

  const periods = periodOptions(granularity);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: '1600px',
            maxWidth: 'calc(100vw - 48px)',
            borderRadius: '12px',
            overflow: 'hidden',
            backgroundColor: '#FFFFFF',
            boxShadow: '0px 24px 48px rgba(145, 158, 171, 0.24)',
          },
        }}
        BackdropProps={{ sx: { backgroundColor: 'rgba(33, 43, 54, 0.48)' } }}
      >
        <Box
          sx={{
            height: '60px',
            minHeight: '60px',
            px: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#FAFBFB',
            borderBottom: '1px solid rgba(145, 158, 171, 0.20)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography
              sx={{
                color: '#333C47',
                fontFamily: 'Public Sans',
                fontSize: '18px',
                fontWeight: 600,
                mr: '16px',
                lineHeight: '100%',
                whiteSpace: 'nowrap',
              }}
            >
              Metric Evolution
            </Typography>
            <ChipSelect
              ariaLabel="Granularity"
              width={120}
              options={GRANULARITY_OPTIONS}
              value={GRANULARITY_OPTIONS.find((option) => option.value === granularity) ?? GRANULARITY_OPTIONS[1]}
              onChange={(option) => changeGranularity(option.value)}
            />
            <ChipSelect
              ariaLabel="Period"
              width={150}
              options={periods}
              value={periods[amount - 1]}
              onChange={(option) => setAmount(option.value)}
            />
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close metric evolution modal" sx={{ color: '#637381' }}>
            <Iconify icon="mingcute:close-line" width={20} />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: '24px 20px 32px' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: `repeat(${MAX_METRICS}, minmax(0, 1fr))` },
              gap: '24px',
            }}
          >
            {slots.map((metric, index) => renderMetricSelect(metric, index))}
          </Box>
          <Box sx={{ mt: 3 }}>
            {chartMetrics.length > 0 ? (
              <ExploreChart
                toggle={toggle}
                filterStore={chartFilters}
                hiddenMetrics={hiddenMetrics}
                showLabels={showLabels}
                matchScale={matchScale}
                onChartMounted={(chartInstance) => {
                  chartInstanceRef.current = chartInstance;
                }}
              />
            ) : (
              <Typography sx={{ py: 10, textAlign: 'center', color: '#919EAB', fontSize: 14 }}>{MONTHLY_ONLY_HINT}</Typography>
            )}
          </Box>
        </DialogContent>
        <Box
          sx={{
            height: '54px',
            px: '20px',
            py: '12px',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid rgba(145,158,171,0.20)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* The two comparisons are exclusive */}
            <CheckboxLabel
              label="Previous Period"
              checked={compareMode === 'previous_period'}
              onChange={(checked) => setCompareMode(checked ? 'previous_period' : 'none')}
            />
            <CheckboxLabel
              label="Year over Year"
              checked={compareMode === 'year_over_year'}
              onChange={(checked) => setCompareMode(checked ? 'year_over_year' : 'none')}
            />
            <Box sx={{ width: '1px', height: '21px', bgcolor: 'rgba(145, 158, 171, 0.4)' }} />
            <CheckboxLabel label="Show labels" checked={showLabels} onChange={setShowLabels} />
            <CheckboxLabel label="Match scales" checked={matchScale} onChange={setMatchScale} />
          </Box>
          <Button
            variant="contained"
            disableElevation
            disabled={chartMetrics.length === 0}
            onClick={() => setExportModalOpen(true)}
            sx={{
              px: '12px',
              py: '6px',
              minWidth: 'auto',
              borderRadius: '8px',
              backgroundColor: '#181818',
              textTransform: 'none',
              fontFamily: 'Public Sans',
              fontSize: '12px',
              fontWeight: 600,
              lineHeight: '18px',
              '&:hover': { backgroundColor: '#181818' },
            }}
          >
            Export chart
          </Button>
        </Box>
      </Dialog>
      <ChartExportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        onExport={(exportFormat, options) => {
          void handleExport(exportFormat, options);
        }}
      />
    </>
  );
}
